// Citation parsing (Chinese 来源 references)
//
// A citation looks like `(来源: <content>)` or `（来源: <content>）`, with mixed
// ASCII / full-width parens allowed and arbitrary paren nesting inside <content>.
// Implementation walks characters and tracks paren depth — no regex, so it
// cannot ReDoS regardless of input. Was previously a regex with an (X+)* shape
// that catastrophically backtracked on unclosed citations and froze Firefox.

const WHITESPACE_RE = /\s/;

/**
 * If a citation begins at `text[start]`, return the index of its closing paren.
 * Otherwise return -1. The citation spans `[start, end]` inclusive.
 */
function findCitationEnd(text, start) {
  const open = text[start];
  if (open !== '(' && open !== '（') return -1;
  if (text[start + 1] !== '来' || text[start + 2] !== '源') return -1;
  const colon = text[start + 3];
  if (colon !== ':' && colon !== '：') return -1;

  let i = start + 4;
  while (i < text.length && WHITESPACE_RE.test(text[i])) i++;

  let depth = 1;
  while (i < text.length) {
    const ch = text[i];
    if (ch === '(' || ch === '（') depth++;
    else if (ch === ')' || ch === '）') {
      depth--;
      if (depth === 0) return i;
    }
    i++;
  }
  return -1;
}

/**
 * Split text into alternating non-citation and citation segments.
 * Always emits a trailing segment (may be empty), matching the prior
 * `text.split(regex)` shape that callers depend on.
 */
export function splitByCitation(text) {
  if (!text) return [];
  const segments = [];
  let segStart = 0;
  let i = 0;
  while (i < text.length) {
    const ch = text[i];
    if (ch === '(' || ch === '（') {
      const end = findCitationEnd(text, i);
      if (end !== -1) {
        segments.push(text.slice(segStart, i));
        segments.push(text.slice(i, end + 1));
        segStart = end + 1;
        i = end + 1;
        continue;
      }
    }
    i++;
  }
  segments.push(text.slice(segStart));
  return segments;
}

/** True iff `segment` is exactly a single citation (no leading/trailing chars). */
export function isCitation(segment) {
  if (!segment) return false;
  const end = findCitationEnd(segment, 0);
  return end === segment.length - 1;
}

/** Return all citation strings found in `text`, in order. */
export function extractCitations(text) {
  if (!text) return [];
  const result = [];
  let i = 0;
  while (i < text.length) {
    const ch = text[i];
    if (ch === '(' || ch === '（') {
      const end = findCitationEnd(text, i);
      if (end !== -1) {
        result.push(text.slice(i, end + 1));
        i = end + 1;
        continue;
      }
    }
    i++;
  }
  return result;
}
